import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAwards } from '../../hooks/useawards';
import { awardsService } from '../../services/dataService';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, Edit2, Trash2, ExternalLink } from 'lucide-react';

const AdminAwards = () => {
  const navigate = useNavigate();
  const { awards, loading, error, refetch } = useAwards();
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (id, title) => {
    if (window.confirm(`Delete "${title}"? This cannot be undone.`)) {
      setDeleting(id);
      try {
        const result = await awardsService.delete(id);
        if (result.success) {
          toast.success('Award deleted successfully');
          refetch();
        } else {
          toast.error('Failed to delete award');
        }
      } catch (error) {
        toast.error('Error deleting award');
      } finally {
        setDeleting(null);
      }
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/adminaccess/dashboard')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <button
            onClick={() => navigate('/adminaccess/awards/new')}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/30 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Award</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-light mb-2">Manage Awards & Recognition</h1>
          <p className="text-white/60">Hackathon wins, scholarships, and achievements</p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="p-8 rounded-lg border border-white/10 bg-white/5 text-center">
            <div className="inline-block w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
            <p className="text-white/60">Loading awards...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-8 rounded-lg border border-red-500/20 bg-red-500/5 text-center">
            <p className="text-red-400">Error: {error}</p>
          </div>
        )}

        {/* Awards List */}
        {!loading && !error && (
          <div className="space-y-3">
            {awards.length === 0 ? (
              <div className="p-12 rounded-lg border border-white/10 bg-white/5 text-center">
                <p className="text-white/40 mb-4">No awards yet</p>
                <button
                  onClick={() => navigate('/adminaccess/awards/new')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/30 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Your First Award</span>
                </button>
              </div>
            ) : (
              awards.map((award) => (
                <div
                  key={award.id}
                  className="p-4 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-medium">{award.title}</h3>
                        <a
                          href={award.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white/40 hover:text-white/60 transition-colors"
                          title="View verification link"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                      <div className="text-white/40 text-sm">{award.date}</div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/adminaccess/awards/edit/${award.id}`)}
                        className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(award.id, award.title)}
                        disabled={deleting === award.id}
                        className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminAwards;